import { useState } from "react";
import { Wrench, CheckCircle, Loader2, FileText, Shield, RefreshCw, AlertTriangle, ThumbsUp, ThumbsDown, Copy, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { toolStreamingLabel } from "../tool-labels";
import {
  parseEnvelope,
  renderCard,
  EnvelopeRenderer,
} from "@/components/generative-ui/envelope";

// Compat barrel re-exports — legacy consumers importing chart cards from
// @/components/cowork/tool-renderers continue to work after the move to
// @/components/generative-ui/charts (WS-2.A T3.1).
export { ChartCard } from "@/components/generative-ui/charts/ChartCard";
export { LeadListCard } from "@/components/generative-ui/charts/LeadListCard";
export { AgendaTimelineCard } from "@/components/generative-ui/charts/AgendaTimelineCard";
export { AutomationsCard } from "@/components/generative-ui/charts/AutomationsCard";

type ToolRendererProps = {
  toolName: string;
  status: "running" | "done";
  result?: string;
};
/* ── Safely extract a human-readable error message from a payload that may be a
 *    string, a Python CanonicalError dict ({user_message, llm_message, ...}),
 *    a Pydantic-shaped error ({code, message, details}), or some other shape.
 *    Returning null tells the caller to fall back to a higher-level summary. ── */
function pickErrorMessage(err: unknown): string | null {
  if (err == null) return null;
  if (typeof err === "string") return err;
  if (typeof err !== "object") return null;
  const e = err as Record<string, unknown>;
  if (typeof e.user_message === "string" && e.user_message.trim()) return e.user_message;
  if (typeof e.message === "string" && e.message.trim()) return e.message;
  if (typeof e.llm_message === "string" && e.llm_message.trim()) return e.llm_message;
  if (typeof e.detail === "string" && e.detail.trim()) return e.detail;
  return null;
}

/* ── Generic data card for tool results ── */
function DataCard({
  icon: Icon,
  title,
  gradient,
  children,
}: {
  icon: React.ElementType;
  title: string;
  gradient: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-[20px] border border-border/50 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_16px_rgba(0,0,0,0.04)]">
      <div className={cn("flex items-center gap-3 px-4 py-3.5 text-white", gradient)}>
        <div className="w-8 h-8 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </div>
        <p className="text-sm font-semibold tracking-tight">{title}</p>
      </div>
      <div className="px-4 py-3.5 bg-card space-y-2">{children}</div>
    </div>
  );
}

function DataRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-foreground">{String(value)}</span>
    </div>
  );
}

/* ── Document Preview Card ── */
function DocumentPreviewCard({ data }: { data: Record<string, unknown> }) {
  return (
    <DataCard icon={FileText} title={String(data.title || data.filename || "Documento")} gradient="bg-app-info">
      {Boolean(data.description) && <p className="text-sm text-muted-foreground">{String(data.description)}</p>}
      {Boolean(data.url) && (
        <a
          href={String(data.url)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-2 text-sm font-medium text-brand hover:underline"
        >
          <FileText className="w-3.5 h-3.5" />
          Baixar documento
        </a>
      )}
    </DataCard>
  );
}
/* ── Reactivation Result Card ──
 * Two distinct flows by design (HITL policy F3.2, 2026-04-19):
 *   - kind="recall"   → trigger_reactivation (messaging.py, per-patient, irreversible)
 *   - kind="campaign" → reactivation_campaign (postcare_agent, cohort, reversible)
 * Same visual scaffold; different title and metric set.
 */
function ReactivationResultCard({ data, kind = "recall" }: { data: Record<string, unknown>; kind?: "recall" | "campaign" }) {
  if (kind === "campaign") {
    const queued = Number(data.queued ?? data.affected_count ?? 0);
    const cutoff = data.cutoff_months != null ? Number(data.cutoff_months) : undefined;
    const campaignId = data.reference_id != null ? String(data.reference_id) : undefined;
    return (
      <DataCard icon={RefreshCw} title="Campanha de Reativação Criada" gradient="bg-brand">
        <DataRow label="Pacientes Enfileirados" value={queued} />
        {cutoff != null && <DataRow label="Corte de Inatividade" value={`${cutoff} mês(es)`} />}
        {campaignId && <DataRow label="ID da Campanha" value={campaignId} />}
      </DataCard>
    );
  }
  return (
    <DataCard icon={RefreshCw} title="Resultado da Reativação" gradient="bg-brand">
      {data.sent != null && <DataRow label="Mensagens Enviadas" value={Number(data.sent)} />}
      {data.delivered != null && <DataRow label="Entregues" value={Number(data.delivered)} />}
      {data.responded != null && <DataRow label="Responderam" value={Number(data.responded)} />}
      {data.reactivated != null && <DataRow label="Reativados" value={Number(data.reactivated)} />}
    </DataCard>
  );
}

/* ── Audit Dashboard Card ── */
function AuditDashboardCard({ data }: { data: Record<string, unknown> }) {
  return (
    <DataCard icon={Shield} title={String(data.title || "Auditoria")} gradient="bg-app-fg-muted">
      {data.score != null && <DataRow label="Score" value={`${data.score}/100`} />}
      {data.issues != null && <DataRow label="Problemas" value={Number(data.issues)} />}
      {Boolean(data.recommendations) && (
        <div className="mt-2">
          <p className="text-xs font-medium text-muted-foreground mb-1">Recomendações:</p>
          <p className="text-sm text-foreground">{String(data.recommendations)}</p>
        </div>
      )}
    </DataCard>
  );
}
/* ── Copy Button ── */
function CopyButton({ data, label }: { data: unknown; label?: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      const text = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <button
      onClick={handleCopy}
      className="inline-flex size-11 items-center justify-center rounded-md text-muted-foreground/40 transition-colors hover:bg-muted/50 hover:text-muted-foreground sm:size-10"
      title={label || "Copiar dados"}
      aria-label={label || "Copiar dados"}
    >
      {copied ? (
        <Check className="w-3.5 h-3.5 text-app-success" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
    </button>
  );
}

/* ── Feedback Buttons ── */
/** Thumbs up / down (and a copy button when there is data behind the card).
 * Exported as `ToolFeedback` so the drawer keeps it on the envelope-first path
 * it renders itself. */
export function ToolFeedback({ toolCallId, data }: { toolCallId?: string; data?: unknown }) {
  const storageKey = `copilot_feedback_${toolCallId || 'unknown'}`;
  const [feedback, setFeedback] = useState<"positive" | "negative" | null>(() => {
    if (!toolCallId) return null;
    try { return localStorage.getItem(storageKey) as any; } catch { return null; }
  });

  function handleFeedback(type: "positive" | "negative") {
    const newValue = feedback === type ? null : type;
    setFeedback(newValue);
    try {
      if (newValue) localStorage.setItem(storageKey, newValue);
      else localStorage.removeItem(storageKey);
    } catch {
      // Feedback remains usable when browser storage is unavailable.
    }
  }

  return (
    <div className="flex items-center gap-1 mt-1.5">
      <button
        onClick={() => handleFeedback("positive")}
        className={cn(
          "inline-flex size-11 items-center justify-center rounded-md transition-colors sm:size-10",
          feedback === "positive"
            ? "text-app-success bg-app-success/10"
            : "text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted/50"
        )}
        title="Resposta útil"
        aria-label="Marcar como útil"
      >
        <ThumbsUp className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => handleFeedback("negative")}
        className={cn(
          "inline-flex size-11 items-center justify-center rounded-md transition-colors sm:size-10",
          feedback === "negative"
            ? "text-app-danger bg-app-danger/10"
            : "text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted/50"
        )}
        title="Resposta pode melhorar"
        aria-label="Marcar como pode melhorar"
      >
        <ThumbsDown className="w-3.5 h-3.5" />
      </button>
      {data !== undefined && <CopyButton data={data} />}
    </div>
  );
}

/* ── Main Router ── */
export function ToolResult({ toolName, status, result, toolCallId }: ToolRendererProps & { toolCallId?: string }): React.ReactNode {
  if (status === "running") {
    // One source of truth for the running copy: the drawer reads the same map.
    return (
      <div className="inline-flex items-center gap-2 rounded-full bg-brand-soft px-3 py-1.5 text-[13px] font-medium text-brand">
        <Loader2 className="size-3.5 motion-safe:animate-spin" aria-hidden="true" />
        {toolStreamingLabel(toolName)}
      </div>
    );
  }

  // ── Envelope-first dispatch (WS-2.B F0, extended in F1) ──
  // New cards emitted by specialized agents and copilot tools come as a
  // ToolOutput envelope (string with `card_kind`, `card`, `has_card`, `error`).
  // parseEnvelope returns null for legacy tool results, so the existing switch
  // below stays the source of truth for everything that hasn't been migrated.
  //
  // Three sub-paths in order:
  //   1. has_card + known kind → CardRegistry render (the original WS-2.B path)
  //   2. has_error             → ErrorCard surfacing CanonicalError fields
  //   3. text-only envelope    → plain bubble showing llm_summary
  //                              (covers the chat_response tool + soft fallbacks)
  // Anything else falls through to the legacy switch.
  if (typeof result === "string") {
    const envelope = parseEnvelope(result);
    if (envelope?.card) {
      const rendered = renderCard(envelope.card);
      if (rendered) {
        return (
          <>
            <EnvelopeRenderer card={envelope.card}>{rendered}</EnvelopeRenderer>
            <ToolFeedback toolCallId={toolCallId} />
          </>
        );
      }
      // rendered === null → unregistered kind → fall through to error/text paths below
    }
    if (envelope?.error) {
      const errMsg = pickErrorMessage(envelope.error) ?? envelope.llm_summary
        ?? "Ocorreu um erro ao executar esta ação.";
      return (
        <>
          <div className="rounded-[20px] border border-app-danger/50 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_16px_rgba(0,0,0,0.04)]">
            <div className="flex items-center gap-3 px-4 py-3.5 bg-app-danger text-white">
              <div className="w-8 h-8 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
                <AlertTriangle className="w-4 h-4" />
              </div>
              <p className="text-sm font-semibold tracking-tight">Não foi possível concluir</p>
            </div>
            <div className="px-4 py-3.5 bg-card space-y-2">
              <p className="text-sm text-app-danger">{errMsg}</p>
              {toolName && <p className="text-xs text-muted-foreground">Ferramenta: {toolName}</p>}
            </div>
          </div>
          <ToolFeedback toolCallId={toolCallId} />
        </>
      );
    }
    if (envelope && !envelope.card && !envelope.error && envelope.llm_summary) {
      // chat_response and other text-only envelopes — render the agent's
      // natural-language line as a plain bubble, not as a tool result card.
      return (
        <>
          <div className="px-4 py-3 rounded-[18px] rounded-tl-md border border-border/30 bg-card/60 text-foreground/85 shadow-[0_0.5px_2px_rgba(0,0,0,0.03)]">
            <div className="cowork-prose prose prose-sm max-w-none">
              {envelope.llm_summary}
            </div>
          </div>
          <ToolFeedback toolCallId={toolCallId} />
        </>
      );
    }
  }

  let data: Record<string, unknown> = {};
  if (result) {
    try {
      data = JSON.parse(result);
    } catch {
      return (
        <>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-muted text-muted-foreground text-xs">
            <CheckCircle className="w-3 h-3 text-app-success" />
            {toolName} concluído
          </div>
          <ToolFeedback toolCallId={toolCallId} />
        </>
      );
    }
  }

  // Check if result indicates an error
  if (data.error || data.status === 'error' || data.detail) {
    return (
      <>
        <div className="rounded-[20px] border border-app-danger/50 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_16px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3 px-4 py-3.5 bg-app-danger text-white">
            <div className="w-8 h-8 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
              <AlertTriangle className="w-4 h-4" />
            </div>
            <p className="text-sm font-semibold tracking-tight">Erro na execução</p>
          </div>
          <div className="px-4 py-3.5 bg-card space-y-2">
            <p className="text-sm text-app-danger">{
              pickErrorMessage(data.error)
              ?? pickErrorMessage(data.detail)
              ?? pickErrorMessage(data.message)
              ?? "Ocorreu um erro ao executar esta ação."
            }</p>
            {toolName && <p className="text-xs text-muted-foreground">Ferramenta: {toolName}</p>}
          </div>
        </div>
        <ToolFeedback toolCallId={toolCallId} data={data} />
      </>
    );
  }

  // Legacy switch — only reached when:
  //   1. parseEnvelope returned null (raw non-envelope tool result), OR
  //   2. The envelope had has_card=true with an unknown kind (today this
  //      cannot happen because KNOWN_KINDS mirrors the Python registry).
  //
  // For tools that emit an envelope with a registered kind (table, chart,
  // tool.completed, ...) the renderer dispatches to CardRegistry above and
  // this switch is dead code for them. Only non-envelope tools — scheduler,
  // coach, audit — and a handful of legacy bare-data emitters route here.
  //
  // Removed cases (dead code, no agent emits these tool names):
  //   - get_today_agenda, get_patient_profile, get_inventory_status
  // Re-add only if a tool with that exact name ships and emits non-envelope
  // data. For new tools, prefer emitting an envelope (kind=table / chart /
  // insight / ...) so this switch stays small.
  const cardContent = (() => { switch (toolName) {
    case "generate_budget_pdf":
    case "generate_treatment_plan":
    case "generate_weekly_report":
    case "export_leads_spreadsheet":
      return <DocumentPreviewCard data={data} />;
    case "audit_conversation":
    case "generate_quality_report":
    case "compliance_check":
      return <AuditDashboardCard data={data} />;
    case "reactivation_campaign":
      return <ReactivationResultCard data={data} kind="campaign" />;
    default:
      return (
        <div className="rounded-[20px] border border-app-warning/50 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_16px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3 px-4 py-3.5 bg-app-warning text-white">
            <div className="w-8 h-8 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
              <Wrench className="w-4 h-4" />
            </div>
            <p className="text-sm font-semibold tracking-tight">Resultado: {toolName}</p>
          </div>
          <div className="px-4 py-3.5 bg-card">
            <pre className="text-xs text-muted-foreground overflow-auto max-h-40 whitespace-pre-wrap break-words">
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
        </div>
      );
  } })();

  return (
    <>
      {cardContent}
      <ToolFeedback toolCallId={toolCallId} data={data} />
    </>
  );
}
